import { FUEL_FLAG_MAPPING } from "../constants/fuelFlag";
import { SUPPLIER_FUEL_TYPE } from "../constants/tariffInfo";

export const formatLabel = (label) => {
  let result = label.replace(/([A-Z])/g, " $1");

  return result.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

export const generateSwitchingFormInitialValues = (
  switchValidationSchema,
  supplierTermsInfo
) => {
  let formData = {};
  Object.keys(switchValidationSchema).forEach((field) => {
    if (switchValidationSchema[field].type === "object") {
      formData[field] = {};
      Object.keys(switchValidationSchema[field].properties).forEach(
        (subField) => {
          const subFieldProperty =
            switchValidationSchema[field].properties[subField];

          if (subFieldProperty.type === "boolean") {
            formData[field][subField] = null;
          } else {
            formData[field][subField] = "";
          }

          if (field === "customerInfo" && subField === "switchType") {
            if (subFieldProperty.valid.length === 1) {
              formData[field][subField] = subFieldProperty.valid[0];
            }
          }
        }
      );

      if (field === "paymentInfo") {
        formData[field].detailsConfirmed = null;
        formData[field].accountHolderConfirmed = null;
        // Only monthly direct is supported for now
        formData[field].preferredPaymentMethod = "Monthly Direct Debit";
      }
      if (field === "customerInfo") {
        formData[field].confirmEmail = "";
      }
    } else if (switchValidationSchema[field].type === "boolean") {
      formData[field] = null;
    } else {
      formData[field] = "";
    }
  });

  // copy the psr field from the supplier terms info into switching form data
  if (supplierTermsInfo?.psr) {
    formData.psr = supplierTermsInfo.psr;
  }

  return formData;
};

export const generateOverrideData = (
  quoteId,
  priceId,
  initialFormValues,
  meterDetails,
  dnoId,
  emailAddress,
  firstName,
  lastName,
  selectedSwitchTariff
) => ({
  quoteId,
  priceId,
  switchType: "DOMESTIC",
  consent: true,
  fuelType: FUEL_FLAG_MAPPING[selectedSwitchTariff?.fuelType],
  meterInfo: {
    ...initialFormValues.meterInfo,
    mpan: meterDetails?.mpan,
    mprn: meterDetails?.mprn,
    fuelType: FUEL_FLAG_MAPPING[selectedSwitchTariff?.fuelType],
  },
  customerInfo: {
    ...initialFormValues.customerInfo,
    dnoId: dnoId ?? "",
    custEmail1: emailAddress,
    custForename: firstName,
    custLastName: lastName,
    custSubBuilding: meterDetails?.subBuilding ?? "",
    custBuildingName: meterDetails?.buildingName ?? "",
    custBuildingNumber: meterDetails?.buildingNumber ?? "",
    custStreet: meterDetails?.street ?? "",
    custDistrict: meterDetails?.district ?? "",
    custCity: meterDetails?.city ?? "",
    custLabel: meterDetails?.label ?? "",
    custProvince: meterDetails?.province ?? "",
    custPostalCode: meterDetails?.postcode ?? "",
    custAddressLine1: meterDetails?.label ?? "",
  },
  consentInfo: {
    ...initialFormValues.consentInfo,
    hasConsentSupplierMarketingPhone: false,
    hasConsentSupplierMarketingSms: false,
    hasConsentSupplierMarketingPost: false,
  },
  metadata: {
    quote: selectedSwitchTariff,
  },
});

export const addPaymentInfoToSchema = (switchValidationSchema) => ({
  ...switchValidationSchema,
  paymentInfo: {
    ...switchValidationSchema.paymentInfo,
    properties: {
      ...switchValidationSchema.paymentInfo.properties,
      detailsConfirmed: {
        required: true,
        type: "boolean",
        valid: [true],
      },
      accountHolderConfirmed: {
        required: true,
        type: "boolean",
        valid: [true],
      },
    },
  },
});

export const addConfirmEmailToSchema = (switchValidationSchema) => ({
  ...switchValidationSchema,
  customerInfo: {
    ...switchValidationSchema.customerInfo,
    properties: {
      ...switchValidationSchema.customerInfo.properties,
      confirmEmail: {
        required: true,
        type: "string",
        format: "email",
        matchField: "custEmail1",
      },
    },
  },
});
export const setMpanMprnRequired = (switchValidationSchema, fuelFlag) => ({
  ...switchValidationSchema,
  meterInfo: {
    ...switchValidationSchema.meterInfo,
    properties: {
      ...switchValidationSchema.meterInfo.properties,
      mpan: {
        ...switchValidationSchema.meterInfo.properties.mpan,
        required: fuelFlag === FUEL_FLAG_MAPPING[SUPPLIER_FUEL_TYPE.ELECGAS] || fuelFlag === FUEL_FLAG_MAPPING[SUPPLIER_FUEL_TYPE.ELEC],
      },
      mprn: {
        ...switchValidationSchema.meterInfo.properties.mprn,
        required: fuelFlag === FUEL_FLAG_MAPPING[SUPPLIER_FUEL_TYPE.ELECGAS] || fuelFlag === FUEL_FLAG_MAPPING[SUPPLIER_FUEL_TYPE.GAS],
      },
    },
  },
});

export const substituteLinkInContent = (data) => {
  // eg data = { content: 'Please visit our website here ', link: { url: 'https://www.example.com', displayText: 'here' } }
  const { content, link } = data;
  if (!content || !link) return null;
  const parts = content.split(link.displayText);

  return (
    <span>
      {parts[0]}
      <a href={link.url} className="text-primary underline">
        {link.displayText}
      </a>
      {parts[1]}
    </span>
  );
};

// Helper function to substitute $1, $2, ... with corresponding links
export const substituteLinksInText = (text, links) => {
  if (!text) return null;
  const parts = text.split(/\$(\d+)/);

  return parts.map((part, index) => {
    // If the part matches a number and there's a corresponding link, render the link
    if (!isNaN(part) && links[part - 1]) {
      const link = links[part - 1];
      if (link?.isURL) {
        return (
          <a
            key={index}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline"
          >
            {link.displayText || link.url}
        </a>
      );
    } else {
      return (
        <span key={index}>
          {link.displayText || link.url}
        </span>
      );
    }
  }
    return <span key={index}>{part}</span>; // Otherwise, render the text as is
  });
};

export const convertStringsToBooleans = (values) => {
  if (typeof values !== "object" || values === null) {
    return values;
  }

  Object.keys(values).forEach((key) => {
    const value = values[key];

    if (typeof value === "string") {
      if (value === "true") {
        values[key] = true;
      } else if (value === "false") {
        values[key] = false;
      }
    } else if (typeof value === "object") {
      convertStringsToBooleans(value);
    }
  });

  return values;
};
